import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import UUID from 'uuid';
import useDidMountEffect from '../../../utils/hooks/useDidMountEffect';
import { closeSection, openSection, transition } from './utils/maxHeightManipulation';
import './C-Alt-Accordion.less';

function Accordion(props) {
    const {
        children,
        isOpen,
        onToggle,
        headerLevel,
        title,
        onClick,
        icon,
        customClass,
        htmlCommonProps,
        htmlTitleProps
    } = props;

    const accordionId = useRef(UUID.v4());
    const [expanded, setIsExpanded] = useState(isOpen);
    const [uiExpanded, setIsUiExpanded] = useState(isOpen);
    const bodyRef = useRef(null);

    const baseClass = 'c-alt-accordion';
    const onClickFn = () => {
        setIsExpanded(!expanded);
        onClick();
    };

    const expandedRef = useRef(expanded);
    const getExpanded = () => expandedRef.current;

    useEffect(() => {
        bodyRef.current.addEventListener('transitionend', transition(getExpanded, setIsUiExpanded));
    }, []);

    useDidMountEffect(() => {
        expandedRef.current = expanded;
        if (expanded) {
            openSection(bodyRef.current);
            setIsUiExpanded(true);
        }
        if (!expanded) closeSection(bodyRef.current);
        onToggle(expanded);
    }, [expanded]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClickFn();
        }
    };

    const HeaderTag = `h${headerLevel}`;

    return (
        <div
            {...htmlCommonProps}
            className={cn(customClass, baseClass, {
                [`${baseClass}__closed`]: !uiExpanded
            })}
        >
            <HeaderTag className={`${baseClass}__heading-container`}>
                <button
                    aria-label={title}
                    onClick={onClickFn}
                    onKeyDown={handleKeyDown}
                    type="button"
                    aria-expanded={uiExpanded}
                    id={accordionId.current}
                    aria-controls={`${accordionId.current}-body`}
                    className={cn(`${baseClass}__header`, {
                        [`${baseClass}__open`]: uiExpanded
                    })}
                    {...htmlTitleProps}
                >
                    <span className={`${baseClass}__title`}>
                        {title}
                    </span>
                    {
                        icon && (
                            <span className={cn(`${baseClass}__icon`, {
                                [`${baseClass}__open`]: expanded
                            })}
                            >
                                {icon}
                            </span>
                        )
                    }

                </button>
            </HeaderTag>
            <div
                role="region"
                aria-labelledby={accordionId.current}
                id={`${accordionId.current}-body`}
                aria-hidden={!uiExpanded}
                ref={bodyRef}
                className={cn(`${baseClass}__body`, {
                    [`${baseClass}__open`]: uiExpanded
                })}
            >
                <div
                    itemProp="text"
                    className={cn(`${baseClass}__content`)}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

Accordion.propTypes = {
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]).isRequired,
    children: PropTypes.node.isRequired,
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
    onClick: PropTypes.func,
    headerLevel: PropTypes.oneOf(['1', '2', '3', '4', '5', '6']),
    icon: PropTypes.element,
    customClass: PropTypes.string,
    htmlCommonProps: PropTypes.shape({}),
    htmlTitleProps: PropTypes.shape({})
};

Accordion.defaultProps = {
    onToggle: () => {},
    onClick: () => {},
    headerLevel: '3',
    isOpen: false,
    icon: null,
    customClass: '',
    htmlCommonProps: {},
    htmlTitleProps: {}
};

export default Accordion;
